import React, { FC, useEffect, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import { getInventoryLedger, InventoryLedgerData, InventoryLedgerMovement, InventoryLedgerProcess } from 'services/inventory/getLedger'

interface InventoryLedgerProps {
  inventoryId: number
  view: 'processes' | 'consumption'
}

const formatAmount = (value: string | number): string => Number(value).toLocaleString('es-CO')
const operationLabels: Record<string, string> = {
  CREATE: 'Creación del inventario',
  CREATED: 'Creación del inventario',
  UPDATE: 'Actualización del inventario',
  UPDATED: 'Actualización del inventario',
  STATE_CHANGED: 'Cambio de estado',
  CREDIT_ADD: 'Añadir créditos',
  CREDIT_USED: 'Consumo de créditos',
  CREDIT_DEDUCT: 'Consumo de créditos',
  CREDIT_RETURN: 'Devolución de créditos',
  RETURN_TEST: 'Devolución de créditos',
  RENEW: 'Renovación del inventario',
  EXPIRATION_CHANGED: 'Cambio de vencimiento',
  DETAILS_UPDATED: 'Actualización de datos',
  ACTIVATE: 'Activación del inventario',
  DEACTIVATE: 'Desactivación del inventario',
}
const operationLabel = (action: string): string => operationLabels[action] || 'Otra operación'
const isFreeBetesaMovement = (movement: InventoryLedgerMovement): boolean =>
  Number(movement.change) === 0 && (movement.notes || '').toLowerCase().includes('nueva oportunidad betesa')
const movementConcept = (movement: InventoryLedgerMovement): string => {
  if (isFreeBetesaMovement(movement)) {
    return (movement.notes || '').toLowerCase().includes('envío confirmado')
      ? 'Nueva oportunidad Betesa enviada (0 créditos)'
      : 'Nueva oportunidad Betesa asignada (0 créditos)'
  }
  if (movement.action === 'CREATE') return 'Saldo inicial'
  if (movement.process_id && Number(movement.change) > 0) return 'Devolución'
  if (movement.process_id && Number(movement.change) < 0) return 'Consumo'
  return 'Ajuste de créditos'
}
const spreadsheetText = (value: string | null): string => {
  const text = value || ''
  return /^[=+@-]/.test(text) ? `'${text}` : text
}

const InventoryLedger: FC<InventoryLedgerProps> = ({ inventoryId, view }) => {
  const [data, setData] = useState<InventoryLedgerData | null>(null)
  const [processId, setProcessId] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedMovement, setSelectedMovement] = useState<InventoryLedgerMovement | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    getInventoryLedger(inventoryId, page, processId)
      .then((result: InventoryLedgerData) => { if (active) setData(result) })
      .catch(() => { if (active) setError('No se pudo cargar el consumo del inventario.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [inventoryId, page, processId])

  const processes = (data?.processes || []).map((process: InventoryLedgerProcess) => ({
    ...process,
    consumed: Number(process.consumed),
    returned: Number(process.returned),
    net: Number(process.consumed) - Number(process.returned),
  }))
  const attributedNet = processes.reduce((sum: number, process) => sum + process.net, 0)
  const movements = (data?.movements || []).map((movement: InventoryLedgerMovement) => ({
    ...movement,
    date: dayjs(movement.created_at).format('DD/MM/YYYY HH:mm'),
    process: movement.process_name || 'Proceso no identificado',
    concept: movementConcept(movement),
    increase: Number(movement.change) > 0 ? formatAmount(movement.change) : '—',
    decrease: isFreeBetesaMovement(movement) ? '0' : Number(movement.change) < 0 ? formatAmount(-Number(movement.change)) : '—',
    balance: data?.inventory.unlimited ? '∞' : formatAmount(movement.balance_after),
  }))

  const downloadExcel = async (): Promise<void> => {
    if (!data || exporting) return
    setExporting(true)
    setExportError('')
    try {
      const firstPage = await getInventoryLedger(inventoryId, 1, processId, 100)
      const allMovements: InventoryLedgerMovement[] = [...firstPage.movements]
      for (let nextPage = 2; nextPage <= firstPage.pagination.total_pages; nextPage += 1) {
        const result = await getInventoryLedger(inventoryId, nextPage, processId, 100)
        allMovements.push(...result.movements)
      }
      if (allMovements.length !== firstPage.pagination.total) throw new Error('Extracto incompleto')

      const rows = allMovements.map((movement: InventoryLedgerMovement) => ({
        ID: movement.id,
        Fecha: dayjs(movement.created_at).format('DD/MM/YYYY HH:mm:ss'),
        Proceso: spreadsheetText(movement.process_name || 'Proceso no identificado'),
        'ID proceso': movement.process_id || '',
        Operación: operationLabel(movement.action),
        'Código de operación': spreadsheetText(movement.action),
        Concepto: movementConcept(movement),
        Entrada: Number(movement.change) > 0 ? Number(movement.change) : 0,
        Salida: Number(movement.change) < 0 ? -Number(movement.change) : 0,
        'Saldo anterior': Number(movement.balance_before),
        'Saldo global': Number(movement.balance_after),
        'Créditos antes': Number(movement.credits_before),
        'Créditos después': Number(movement.credits_after),
        'Usados antes': Number(movement.used_before),
        'Usados después': Number(movement.used_after),
        'Registrado por': spreadsheetText(movement.actor_name),
        'ID usuario': movement.user_id || '',
        Notas: spreadsheetText(movement.notes),
      }))
      const worksheet = XLSX.utils.json_to_sheet(rows)
      worksheet['!cols'] = [{ wch: 10 }, { wch: 20 }, { wch: 35 }, { wch: 12 }, { wch: 28 }, { wch: 22 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 32 }, { wch: 12 }, { wch: 70 }]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos')
      XLSX.writeFile(workbook, `consumo-inventario-${inventoryId}${processId ? `-proceso-${processId}` : ''}.xlsx`)
    } catch {
      setExportError('No se pudo descargar el extracto completo. Inténtalo de nuevo.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box sx={{ background: '#fff', borderRadius: '12px', p: { xs: 2, md: 3 }, boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{view === 'processes' ? 'Procesos del inventario' : 'Consumo del inventario'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {view === 'processes'
          ? 'Procesos identificados en los movimientos de este inventario.'
          : 'El saldo de cada movimiento es global. Al filtrar, las entradas y salidas corresponden solo al proceso seleccionado.'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
      {data && <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, p: 2, mb: 3, background: '#eaf3f8', borderRadius: '10px', color: '#173b54' }}>
          <span>Créditos asignados: <strong>{data.inventory.unlimited ? '∞' : formatAmount(data.inventory.credits)}</strong></span>
          <span>Consumo actual: <strong>{formatAmount(data.inventory.tests_used)}</strong></span>
          <span>Disponibles: <strong>{data.inventory.unlimited ? '∞' : formatAmount(data.inventory.available)}</strong></span>
        </Box>

        {attributedNet !== Number(data.inventory.tests_used) && <Alert severity="info" sx={{ mb: 2 }}>
          El consumo atribuido a procesos ({formatAmount(attributedNet)}) no coincide con el consumo actual ({formatAmount(data.inventory.tests_used)}).
          {' '}Hay movimientos históricos sin una atribución verificable; el saldo global procede del inventario.
        </Alert>}
        {view === 'processes' && <>
        <Typography variant="h6" sx={{ mb: 1 }}>Procesos identificados en el historial</Typography>
        {processes.length ? <BluesiteTable
          data={processes}
          headers={[
            { key: 'name', name: 'Proceso' },
            { key: 'consumed', name: 'Consumidos', render: (row: typeof processes[number]) => formatAmount(row.consumed) },
            { key: 'returned', name: 'Devueltos', render: (row: typeof processes[number]) => formatAmount(row.returned) },
            { key: 'net', name: 'Consumo neto', render: (row: typeof processes[number]) => formatAmount(row.net) },
          ]}
          searchBar={false}
          searchLabel=""
          showNumbers={false}
          tableStyle="inventory-ledger-processes"
          minimumColumnWidth={120}
        /> : <Typography color="text.secondary" sx={{ mb: 3 }}>Todavía no hay movimientos vinculados a procesos.</Typography>}
        </>}

        {view === 'consumption' && <>
        <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: 2, my: 3 }}>
          <Typography variant="h6">Extracto de movimientos</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="ledger-process-label">Filtrar por proceso</InputLabel>
            <Select labelId="ledger-process-label" label="Filtrar por proceso" value={processId || ''} onChange={(event) => { setProcessId(event.target.value ? Number(event.target.value) : null); setPage(1) }}>
              <MenuItem value="">Todos los procesos</MenuItem>
              {processes.map((process) => <MenuItem key={process.id} value={process.id}>{process.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" disabled={loading || exporting || !data || data.pagination.total === 0} onClick={downloadExcel} sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>
            {exporting ? 'Preparando Excel...' : 'Descargar Excel'}
          </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Salidas: {formatAmount(data.totals.consumed)} · Entradas: {formatAmount(data.totals.increases)} · Movimientos: {data.pagination.total}
        </Typography>
        <BluesiteTable
          data={movements}
          headers={[
            { key: 'date', name: 'Fecha' },
            { key: 'process', name: 'Proceso' },
            { key: 'concept', name: 'Concepto', render: (row: typeof movements[number]) => <Box title={row.notes || ''}>
              <strong>{row.concept}</strong>
              {row.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{row.notes}</Typography>}
            </Box> },
            { key: 'increase', name: 'Entrada' },
            { key: 'decrease', name: 'Salida' },
            { key: 'balance', name: 'Saldo global' },
            { key: 'detail', name: 'Detalle', render: (row: typeof movements[number]) => <Tooltip title="Ver detalle del movimiento">
              <IconButton aria-label="Ver detalle del movimiento" size="small" onClick={() => setSelectedMovement(row)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip> },
          ]}
          searchBar={false}
          searchLabel=""
          showNumbers={false}
          loading={loading}
          currentPage={page}
          totalPages={data.pagination.total_pages}
          handlePagination={setPage}
          tableStyle="inventory-ledger"
          minimumColumnWidth={120}
        />
        {!loading && movements.length === 0 && <Typography color="text.secondary" sx={{ mt: 2 }}>No hay movimientos para este filtro.</Typography>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Los movimientos históricos sin cifras completas no aparecen en el extracto. Un proceso solo se atribuye cuando puede identificarse en el registro.
        </Typography>
        </>}
      </>}
      {loading && !data && <Typography color="text.secondary">Cargando movimientos...</Typography>}
      <Dialog open={selectedMovement !== null} onClose={() => setSelectedMovement(null)} fullWidth maxWidth="sm">
        <DialogTitle>Detalle del movimiento #{selectedMovement?.id}</DialogTitle>
        <DialogContent>
          {selectedMovement && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, '& strong': { color: '#173b54' } }}>
            <span><strong>Fecha:</strong> {dayjs(selectedMovement.created_at).format('DD/MM/YYYY HH:mm:ss')}</span>
            <span><strong>Proceso:</strong> {selectedMovement.process_name || 'No identificado'}{selectedMovement.process_id ? ` (#${selectedMovement.process_id})` : ''}</span>
            <span><strong>Operación:</strong> {operationLabel(selectedMovement.action)}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Código: {selectedMovement.action}</Typography></span>
            <span><strong>Registrado por:</strong> {selectedMovement.actor_name}{selectedMovement.user_id ? ` (#${selectedMovement.user_id})` : ''}</span>
            <span><strong>Créditos antes:</strong> {formatAmount(selectedMovement.credits_before)}</span>
            <span><strong>Créditos después:</strong> {formatAmount(selectedMovement.credits_after)}</span>
            <span><strong>Usados antes:</strong> {formatAmount(selectedMovement.used_before)}</span>
            <span><strong>Usados después:</strong> {formatAmount(selectedMovement.used_after)}</span>
            <span><strong>Saldo antes:</strong> {data?.inventory.unlimited ? '∞' : formatAmount(selectedMovement.balance_before)}</span>
            <span><strong>Saldo después:</strong> {data?.inventory.unlimited ? '∞' : formatAmount(selectedMovement.balance_after)}</span>
            <Box sx={{ gridColumn: '1 / -1', overflowWrap: 'anywhere' }}><strong>Notas:</strong> {selectedMovement.notes || 'Sin notas'}</Box>
          </Box>}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedMovement(null)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  )
}

export default InventoryLedger
